import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, DataTable, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, RotateCcw, Wallet, Clock, Banknote, CheckCircle2, Landmark, CheckCheck, Upload, History, FileSpreadsheet, AlertTriangle, Loader2, Settings, Download, Ban, Send } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { can } from "@/lib/permissions";

const PAGE_SIZE = 50;
const ALL = "all";
const TABS = [
  { id: "awaiting", label: "Awaiting" },
  { id: "collected", label: "Pending Payout" },
  { id: "in_payout", label: "In Payout" },
  { id: "paid", label: "Paid" },
  { id: ALL, label: "All" },
];
const maskAcct = (n) => { const s = String(n || ""); return s.length <= 4 ? s : "•••• " + s.slice(-4); };
const DEFAULT_WINDOW_DAYS = 30;
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const inr = (n) => (n == null ? "₹0" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const CodBadge = ({ status }) => {
  const map = {
    awaiting: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Awaiting" },
    collected: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending payout" },
    in_payout: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "In payout" },
    paid: { cls: "bg-green-50 text-green-700 border-green-200", label: "Paid" },
  };
  const m = map[status] || map.awaiting;
  return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
};

const BATCH_STATUS = {
  initiated: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Initiated" },
  partially_paid: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Partially paid" },
  paid: { cls: "bg-green-50 text-green-700 border-green-200", label: "Paid" },
  cancelled: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Cancelled" },
};
const BatchStatusBadge = ({ status }) => {
  const m = BATCH_STATUS[status] || BATCH_STATUS.initiated;
  return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
};

// Vendors whose COD remittance export we can parse (must match backend VENDORS).
const COD_VENDORS = [{ id: "expressfly", label: "ExpressFly" }];

const OUTCOME_META = {
  collected: { cls: "bg-green-50 text-green-700 border-green-200", label: "Moved to payout" },
  already_collected: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Already collected" },
  already_paid: { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Already paid" },
  not_found: { cls: "bg-red-50 text-red-700 border-red-200", label: "No matching AWB" },
};

const StatChip = ({ label, value, cls }) => (
  <div className={`rounded-lg border px-3 py-2 ${cls}`}>
    <p className="text-lg font-bold tabular-nums leading-none">{value ?? 0}</p>
    <p className="text-[11px] mt-1 leading-tight">{label}</p>
  </div>
);

// Reconciliation summary shown after an upload (and reused inside the detail view).
const UploadSummary = ({ r }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      <StatChip label="Rows in file" value={r.total_rows} cls="bg-slate-50 border-slate-200 text-slate-700" />
      <StatChip label="Matched (AWB)" value={r.matched_count} cls="bg-slate-50 border-slate-200 text-slate-700" />
      <StatChip label="Moved to payout" value={r.collected_count} cls="bg-green-50 border-green-200 text-green-700" />
      <StatChip label="Already collected" value={r.already_collected_count} cls="bg-amber-50 border-amber-200 text-amber-700" />
      <StatChip label="Already paid" value={r.already_paid_count} cls="bg-blue-50 border-blue-200 text-blue-700" />
      <StatChip label="Unmatched" value={r.unmatched_count} cls="bg-red-50 border-red-200 text-red-700" />
    </div>
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
      <span className="text-sm text-slate-600">Collected amount moved to payout</span>
      <span className="text-base font-bold tabular-nums text-slate-800">{inr(r.collected_amount)}</span>
    </div>
    {r.amount_mismatch_count > 0 && (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          {r.amount_mismatch_count} order(s) had a COD amount that differs from our record — flagged for review (still moved to payout).
        </p>
      </div>
    )}
  </div>
);

const B2CCodReconciliation = ({ user, onLogout }) => {
  const canPayout = can(user, "b2c.cod:payout");

  const [activeTab, setActiveTab] = useLocalStorageState("admin-b2c-cod:tab", "collected");
  const [sellerId, setSellerId] = useLocalStorageState("admin-b2c-cod:seller", ALL);
  const [aggregator, setAggregator] = useLocalStorageState("admin-b2c-cod:aggregator", ALL);
  const [dateFrom, setDateFrom] = useLocalStorageState("admin-b2c-cod:date_from", daysAgoISO(DEFAULT_WINDOW_DAYS));
  const [dateTo, setDateTo] = useLocalStorageState("admin-b2c-cod:date_to", "");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], total: 0, summary: {} });
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ sellers: [], aggregators: [] });
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [collecting, setCollecting] = useState(false);

  // Payout modal
  const [payoutSeller, setPayoutSeller] = useState(null); // {seller_id, brand_name}
  const [payoutOrders, setPayoutOrders] = useState([]);
  const [payoutSel, setPayoutSel] = useState(() => new Set());
  const [utr, setUtr] = useState("");
  const [notes, setNotes] = useState("");
  const [paying, setPaying] = useState(false);
  const [loadingPayout, setLoadingPayout] = useState(false);

  // COD remittance file upload + audit history
  const fileRef = useRef(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadVendor, setUploadVendor] = useState(COD_VENDORS[0].id);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadDetail, setUploadDetail] = useState(null); // full doc of a selected upload
  const [detailLoading, setDetailLoading] = useState(false);

  // COD payout: ICICI config, batch generation, batches history + per-seller UTR
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgForm, setCfgForm] = useState({});
  const [cfgSaving, setCfgSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchDetail, setBatchDetail] = useState(null); // full batch doc
  const [batchLoading, setBatchLoading] = useState(false);
  const [utrInputs, setUtrInputs] = useState({}); // {seller_id: utr}
  const [utrBusy, setUtrBusy] = useState(null);    // seller_id being recorded
  const [cancelling, setCancelling] = useState(false);

  const abortRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/b2c/admin/cod/filter-options`)
      .then((r) => setOptions({ sellers: r.data?.sellers || [], aggregators: r.data?.aggregators || [] }))
      .catch(() => {});
  }, []);

  const loadPending = useCallback(() => {
    axios.get(`${API}/b2c/admin/cod/pending-by-seller`)
      .then((r) => setPending(r.data?.items || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchCod = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeTab !== ALL) params.status_group = activeTab;
      if (sellerId !== ALL) params.seller_id = sellerId;
      if (aggregator !== ALL) params.aggregator = aggregator;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/b2c/admin/cod`, { params, signal: controller.signal });
      setData({ items: res.items || [], total: res.total || 0, summary: res.summary || {} });
      setSelected(new Set());
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      toast.error(errDetail(e, "Failed to load COD reconciliation"));
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, sellerId, aggregator, dateFrom, dateTo, search]);

  useEffect(() => { fetchCod(); }, [fetchCod]);
  useEffect(() => { setPage(1); }, [activeTab, sellerId, aggregator, dateFrom, dateTo, search]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const doCollect = async () => {
    if (!selected.size) return;
    setCollecting(true);
    try {
      const { data: res } = await axios.post(`${API}/b2c/admin/cod/collect`, { order_ids: [...selected] });
      toast.success(`Marked ${res.collected_count} order(s) collected (${inr(res.total_amount)}).`);
      fetchCod(); loadPending();
    } catch (e) {
      toast.error(errDetail(e, "Could not mark collected"));
    } finally {
      setCollecting(false);
    }
  };

  const openPayout = async (seller) => {
    setPayoutSeller(seller);
    setUtr(""); setNotes(""); setPayoutOrders([]); setPayoutSel(new Set());
    setLoadingPayout(true);
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/cod`, {
        params: { seller_id: seller.seller_id, status_group: "collected", page_size: 200 },
      });
      const items = res.items || [];
      setPayoutOrders(items);
      setPayoutSel(new Set(items.map((o) => o.id))); // select all by default
    } catch (e) {
      toast.error(errDetail(e, "Could not load this seller's collected COD"));
    } finally {
      setLoadingPayout(false);
    }
  };

  const togglePayout = (id) => setPayoutSel((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const payoutTotal = payoutOrders.filter((o) => payoutSel.has(o.id)).reduce((s, o) => s + (o.cod_amount || 0), 0);

  const doPayout = async () => {
    if (!payoutSeller || !payoutSel.size || utr.trim().length < 3) {
      toast.error("Select orders and enter a valid bank-transfer reference (UTR).");
      return;
    }
    setPaying(true);
    try {
      const { data: res } = await axios.post(`${API}/b2c/admin/cod/payout`, {
        seller_id: payoutSeller.seller_id,
        order_ids: [...payoutSel],
        utr: utr.trim(),
        notes: notes.trim() || null,
      });
      toast.success(`Payout ${res.payout_number} recorded — ${inr(res.amount)} to ${payoutSeller.brand_name || payoutSeller.seller_id}.`);
      setPayoutSeller(null);
      fetchCod(); loadPending();
    } catch (e) {
      toast.error(errDetail(e, "Could not record the payout"));
    } finally {
      setPaying(false);
    }
  };

  const resetUpload = () => {
    setUploadFile(null); setUploadResult(null); setUploadVendor(COD_VENDORS[0].id);
    if (fileRef.current) fileRef.current.value = "";
  };

  const doUpload = async () => {
    if (!uploadFile) { toast.error("Choose a COD remittance file to upload."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("vendor", uploadVendor);
      fd.append("file", uploadFile);
      const { data: res } = await axios.post(`${API}/b2c/admin/cod/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res);
      toast.success(`Reconciled ${res.total_rows} row(s) — ${res.collected_count} moved to Pending payout.`);
      fetchCod(); loadPending();
      if (historyOpen) loadUploads();
    } catch (e) {
      toast.error(errDetail(e, "Could not process the COD file"));
    } finally {
      setUploading(false);
    }
  };

  const loadUploads = useCallback(async () => {
    setUploadsLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/cod/uploads`, { params: { page: 1, page_size: 50 } });
      setUploads(res.items || []);
    } catch (e) {
      toast.error(errDetail(e, "Could not load upload history"));
    } finally {
      setUploadsLoading(false);
    }
  }, []);

  const openUploadDetail = async (id) => {
    setDetailLoading(true);
    setUploadDetail({ id });
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/cod/uploads/${id}`);
      setUploadDetail(res);
    } catch (e) {
      toast.error(errDetail(e, "Could not load upload detail"));
      setUploadDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // ---- COD payout: config ----
  const loadCfg = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/cod/payout-config`);
      setCfg(res);
      return res;
    } catch (e) { /* view-only users still see the page */ return null; }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const openConfig = async () => {
    const res = (await loadCfg()) || cfg || {};
    setCfgForm({
      debit_account_number: res.debit_account_number || "",
      payment_product_type: res.payment_product_type || "PAB_VENDOR",
      payment_mode: res.payment_mode || "IMPS",
      debit_narr_template: res.debit_narr_template || "{ref} {beneficiary}",
      credit_narr_template: res.credit_narr_template || "COD payout {batch}",
      mobile_num: res.mobile_num || "", email_id: res.email_id || "", remark: res.remark || "",
    });
    setCfgOpen(true);
  };

  const saveConfig = async () => {
    setCfgSaving(true);
    try {
      const { data: res } = await axios.put(`${API}/b2c/admin/cod/payout-config`, cfgForm);
      setCfg(res);
      toast.success("COD payout settings saved.");
      setCfgOpen(false);
    } catch (e) {
      toast.error(errDetail(e, "Could not save settings"));
    } finally {
      setCfgSaving(false);
    }
  };

  // ---- COD payout: batches ----
  const loadBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/cod/payout-batches`, { params: { page: 1, page_size: 50 } });
      setBatches(res.items || []);
    } catch (e) {
      toast.error(errDetail(e, "Could not load payout batches"));
    } finally {
      setBatchesLoading(false);
    }
  }, []);

  const openBatchDetail = async (id) => {
    setBatchLoading(true);
    setBatchDetail({ id });
    setUtrInputs({});
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/cod/payout-batches/${id}`);
      setBatchDetail(res);
    } catch (e) {
      toast.error(errDetail(e, "Could not load the batch"));
      setBatchDetail(null);
    } finally {
      setBatchLoading(false);
    }
  };

  const doGenerate = async () => {
    if (!cfg?.is_debit_account_set) {
      toast.error("Set the debit account number in Payout settings first.");
      openConfig();
      return;
    }
    setGenerating(true);
    try {
      const { data: res } = await axios.post(`${API}/b2c/admin/cod/payout-batches`, {});
      toast.success(`Payout file ${res.batch_number} generated — ${res.seller_count} seller(s), ${inr(res.total_net)}${res.exception_count ? `, ${res.exception_count} skipped (no bank)` : ""}.`);
      fetchCod(); loadPending();
      setBatchesOpen(false);
      openBatchDetail(res.id);
      loadBatches();
    } catch (e) {
      toast.error(errDetail(e, "Could not generate the payout file"));
    } finally {
      setGenerating(false);
    }
  };

  const downloadBatchFile = async (id, number) => {
    try {
      const res = await axios.get(`${API}/b2c/admin/cod/payout-batches/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ICICI_COD_Payout_${number || id}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errDetail(e, "Could not download the file"));
    }
  };

  const submitSellerUtr = async (sellerId) => {
    const utrVal = (utrInputs[sellerId] || "").trim();
    if (utrVal.length < 3) { toast.error("Enter the bank UTR for this seller."); return; }
    setUtrBusy(sellerId);
    try {
      const { data: res } = await axios.post(
        `${API}/b2c/admin/cod/payout-batches/${batchDetail.id}/sellers/${sellerId}/utr`,
        { utr: utrVal },
      );
      toast.success(`Payout ${res.payout.payout_number} recorded — ${inr(res.payout.net_amount ?? res.payout.amount)}.`);
      setBatchDetail(res.batch);
      setUtrInputs((p) => { const n = { ...p }; delete n[sellerId]; return n; });
      fetchCod(); loadPending(); loadBatches();
    } catch (e) {
      toast.error(errDetail(e, "Could not record the UTR"));
    } finally {
      setUtrBusy(null);
    }
  };

  const doCancelBatch = async (id) => {
    setCancelling(true);
    try {
      const { data: res } = await axios.post(`${API}/b2c/admin/cod/payout-batches/${id}/cancel`, {});
      toast.success(`Batch ${res.batch_number} cancelled — ${res.order_count} order(s) back to Pending payout.`);
      setBatchDetail(res);
      fetchCod(); loadPending(); loadBatches();
    } catch (e) {
      toast.error(errDetail(e, "Could not cancel the batch"));
    } finally {
      setCancelling(false);
    }
  };

  const summary = data.summary || {};
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const selectableShown = data.items.some((o) => o.cod_status === "awaiting");

  const columns = [
    ...(canPayout ? [{
      key: "sel", header: "", isActions: true,
      cell: (o) => o.cod_status === "awaiting"
        ? <Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} onClick={(e) => e.stopPropagation()} />
        : <span className="text-slate-300">—</span>,
    }] : []),
    { key: "order", header: "Reference #", primary: true, cell: (o) => (
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-medium text-slate-800">{o.reference_number || "-"}</span>
          <span className="text-xs text-slate-400">{o.order_number}</span>
        </div>
      ) },
    { key: "seller", header: "Seller", cell: (o) => (
      <div>
        <span className="text-sm text-slate-700">{o.seller_brand_name || o.seller_id}</span>
        <SellerBalanceById sellerId={o.seller_id} />
      </div>
    ) },
    { key: "awb", header: "AWB", hideOnMobile: true, cell: (o) => o.awb ? <span className="text-sm font-mono text-slate-700">{o.awb}</span> : <span className="text-slate-400">-</span> },
    { key: "aggregator", header: "Aggregator", hideOnMobile: true, cell: (o) => <span className="text-sm capitalize text-slate-600">{o.aggregator || "-"}</span> },
    { key: "cod", header: "COD", align: "right", cell: (o) => <span className="text-sm font-semibold tabular-nums text-slate-800">{inr(o.cod_amount)}</span> },
    { key: "status", header: "COD status", cell: (o) => <CodBadge status={o.cod_status} /> },
    { key: "payout", header: "Payout", hideOnMobile: true, cell: (o) => o.cod_payout_number ? <span className="text-xs font-mono text-slate-600">{o.cod_payout_number}</span> : <span className="text-slate-400">-</span> },
  ];

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <PageHeader
            title="COD Reconciliation"
            subtitle="COD collected across sellers, reconciled and paid out to seller banks"
            className="lg:flex-1 min-w-0"
          />
          <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:shrink-0">
            <Button variant="outline" size="sm" onClick={() => { setBatchesOpen(true); loadBatches(); }}>
              <Landmark className="w-4 h-4 mr-1.5" /> Payout batches
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setHistoryOpen(true); loadUploads(); }}>
              <History className="w-4 h-4 mr-1.5" /> Upload history
            </Button>
            {canPayout && (
              <Button size="sm" onClick={() => { resetUpload(); setUploadOpen(true); }}>
                <Upload className="w-4 h-4 mr-1.5" /> Upload COD file
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Awaiting collection" value={inr(summary.awaiting?.amount)} icon={Clock} accent="neutral" loading={loading} trend={`${summary.awaiting?.count ?? 0} orders`} />
          <StatCard label="Pending payout" value={inr(summary.collected?.amount)} icon={Banknote} accent="warning" loading={loading} trend={`${summary.collected?.count ?? 0} orders`} />
          <StatCard label="In payout" value={inr(summary.in_payout?.amount)} icon={Landmark} accent="neutral" loading={loading} trend={`${summary.in_payout?.count ?? 0} awaiting UTR`} />
          <StatCard label="Paid to sellers" value={inr(summary.paid?.amount)} icon={CheckCircle2} accent="success" loading={loading} trend={`${summary.paid?.count ?? 0} orders`} />
        </div>

        {/* Payout worklist: collected-but-unpaid by seller */}
        {pending.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-300">
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Landmark className="w-4 h-4 text-amber-600" /> Pending payouts ({pending.length} seller{pending.length > 1 ? "s" : ""})
              </CardTitle>
              {canPayout && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={openConfig} title="ICICI payout settings">
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={doGenerate} disabled={generating}>
                    {generating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</>
                      : <><Download className="w-4 h-4 mr-1.5" /> Generate payout file</>}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pending.map((p) => (
                  <div key={p.seller_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.brand_name || p.seller_id}</p>
                      <p className="text-xs text-slate-500">{p.collected_count} order(s) · <span className="font-semibold text-slate-700">{inr(p.collected_amount)}</span></p>
                    </div>
                    {canPayout && (
                      <Button size="sm" onClick={() => openPayout(p)}>Record payout</Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Seller</Label>
                <Select value={sellerId} onValueChange={setSellerId}>
                  <SelectTrigger><SelectValue placeholder="All sellers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sellers</SelectItem>
                    {options.sellers.map((s) => <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.seller_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Aggregator</Label>
                <Select value={aggregator} onValueChange={setAggregator}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All aggregators</SelectItem>
                    {options.aggregators.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">From</Label>
                <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">To</Label>
                <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input placeholder="Order #, AWB or phone..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs + collect action */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === t.id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                }`}>{t.label}</button>
            ))}
          </div>
          {canPayout && selected.size > 0 && (
            <Button size="sm" onClick={doCollect} disabled={collecting}>
              <CheckCheck className="w-4 h-4 mr-1.5" /> Mark {selected.size} collected
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(o) => o.id}
          loading={loading}
          empty={<EmptyState icon={Wallet} title="No COD orders match these filters" description="COD orders appear here once sellers ship cash-on-delivery parcels." />}
          pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
        />
        {canPayout && activeTab === "awaiting" && selectableShown && selected.size === 0 && (
          <p className="text-xs text-slate-500">Tick orders we've received the COD for, then “Mark collected”.</p>
        )}
      </div>

      {/* Record payout modal */}
      <Dialog open={!!payoutSeller} onOpenChange={(v) => !v && setPayoutSeller(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Record COD payout — {payoutSeller?.brand_name || payoutSeller?.seller_id}</DialogTitle>
          </DialogHeader>
          {loadingPayout ? (
            <p className="text-sm text-slate-500 py-6">Loading collected COD orders…</p>
          ) : payoutOrders.length === 0 ? (
            <p className="text-sm text-slate-500 py-6">No collected-but-unpaid COD orders for this seller.</p>
          ) : (
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {payoutOrders.map((o) => (
                  <label key={o.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={payoutSel.has(o.id)} onCheckedChange={() => togglePayout(o.id)} />
                    <span className="flex-1 min-w-0 text-sm">
                      <span className="font-medium text-slate-800">{o.order_number}</span>
                      <span className="text-slate-500"> · {o.buyer_name || "-"}</span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-slate-800">{inr(o.cod_amount)}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-600">{payoutSel.size} order(s) selected</span>
                <span className="text-base font-bold tabular-nums text-slate-800">{inr(payoutTotal)}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cod-utr" className="text-xs text-slate-600">Bank transfer reference (UTR) <span className="text-red-500">*</span></Label>
                  <Input id="cod-utr" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. NEFT/IMPS UTR" className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cod-notes" className="text-xs text-slate-600">Notes (optional)</Label>
                  <Input id="cod-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal note" />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Full COD is paid to the seller's bank on file. This records the transfer and marks the orders paid — it can't be undone here.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutSeller(null)} disabled={paying}>Cancel</Button>
            <Button onClick={doPayout} disabled={paying || !payoutSel.size || utr.trim().length < 3}>
              {paying ? "Recording…" : `Record payout ${inr(payoutTotal)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload COD remittance file */}
      <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) resetUpload(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload COD remittance file</DialogTitle>
          </DialogHeader>
          {!uploadResult ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Vendor</Label>
                <Select value={uploadVendor} onValueChange={setUploadVendor}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COD_VENDORS.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Remittance file (.xlsx)</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:opacity-90"
                />
                {uploadFile && (
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    <FileSpreadsheet className="w-3.5 h-3.5" /> {uploadFile.name}
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Rows are matched by <span className="font-medium">AWB</span>; matched orders move to
                {" "}<span className="font-medium">Pending payout</span>. Amount differences are flagged, not blocked. No wallet is touched.
              </p>
            </div>
          ) : (
            <UploadSummary r={uploadResult} />
          )}
          <DialogFooter>
            {!uploadResult ? (
              <>
                <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
                <Button onClick={doUpload} disabled={uploading || !uploadFile}>
                  {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Reconciling…</> : "Upload & reconcile"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={resetUpload}>Upload another</Button>
                <Button onClick={() => setUploadOpen(false)}>Done</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload history (audit) */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>COD upload history</DialogTitle>
          </DialogHeader>
          {uploadsLoading ? (
            <p className="text-sm text-slate-500 py-6">Loading…</p>
          ) : uploads.length === 0 ? (
            <p className="text-sm text-slate-500 py-6">No COD files have been uploaded yet.</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                    <th className="py-2 px-2 font-medium">File</th>
                    <th className="py-2 px-2 font-medium">Vendor</th>
                    <th className="py-2 px-2 font-medium">Uploaded by</th>
                    <th className="py-2 px-2 font-medium whitespace-nowrap">When</th>
                    <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Collected</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((u) => (
                    <tr key={u.id} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 px-2 max-w-[180px] truncate" title={u.filename}>{u.filename || "-"}</td>
                      <td className="py-2 px-2">{u.vendor_label || u.vendor}</td>
                      <td className="py-2 px-2 text-slate-600">{u.uploaded_by?.user_name || u.uploaded_by?.user_email || "-"}</td>
                      <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{formatToIST(u.uploaded_at)}</td>
                      <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{u.collected_count}/{u.total_rows}</td>
                      <td className="py-2 px-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openUploadDetail(u.id)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* One upload's per-row reconciliation detail */}
      <Dialog open={!!uploadDetail} onOpenChange={(v) => !v && setUploadDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Reconciliation detail{uploadDetail?.filename ? ` — ${uploadDetail.filename}` : ""}</DialogTitle>
          </DialogHeader>
          {detailLoading || !uploadDetail?.results ? (
            <p className="text-sm text-slate-500 py-6">Loading…</p>
          ) : (
            <div className="space-y-4">
              <UploadSummary r={uploadDetail} />
              <div className="max-h-[45vh] overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                      <th className="py-2 px-2 font-medium">AWB</th>
                      <th className="py-2 px-2 font-medium">Order #</th>
                      <th className="py-2 px-2 font-medium text-right">File COD</th>
                      <th className="py-2 px-2 font-medium text-right">Our COD</th>
                      <th className="py-2 px-2 font-medium">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadDetail.results.map((row, i) => {
                      const meta = OUTCOME_META[row.outcome] || OUTCOME_META.not_found;
                      return (
                        <tr key={`${row.awb}-${i}`} className="border-b border-slate-50 last:border-0">
                          <td className="py-1.5 px-2 font-mono text-slate-700">{row.awb}</td>
                          <td className="py-1.5 px-2 text-slate-600">{row.order_number || row.order_no || "-"}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-slate-700">{row.cod_amount == null ? "-" : inr(row.cod_amount)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-slate-500">{row.order_cod == null ? "-" : inr(row.order_cod)}</td>
                          <td className="py-1.5 px-2">
                            <span className="inline-flex items-center gap-1">
                              <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                              {row.amount_mismatch && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="COD amount differs from our record" />}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {uploadDetail.results_truncated && (
                <p className="text-xs text-slate-400">Showing the first {uploadDetail.results.length} rows (detail is capped for very large files).</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ICICI payout settings */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>COD payout settings (ICICI file)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Debit account number <span className="text-red-500">*</span></Label>
              <Input value={cfgForm.debit_account_number || ""} onChange={(e) => setCfgForm((f) => ({ ...f, debit_account_number: e.target.value }))}
                placeholder="Your ICICI current account" className="font-mono" />
              <p className="text-[11px] text-slate-400">The account COD payouts are debited from. Required before a file can be generated.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Payment mode</Label>
                <Input value={cfgForm.payment_mode || ""} onChange={(e) => setCfgForm((f) => ({ ...f, payment_mode: e.target.value }))} placeholder="IMPS" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Product type</Label>
                <Input value={cfgForm.payment_product_type || ""} onChange={(e) => setCfgForm((f) => ({ ...f, payment_product_type: e.target.value }))} placeholder="PAB_VENDOR" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Debit narration template</Label>
              <Input value={cfgForm.debit_narr_template || ""} onChange={(e) => setCfgForm((f) => ({ ...f, debit_narr_template: e.target.value }))} className="font-mono text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Credit narration template</Label>
              <Input value={cfgForm.credit_narr_template || ""} onChange={(e) => setCfgForm((f) => ({ ...f, credit_narr_template: e.target.value }))} className="font-mono text-xs" />
              <p className="text-[11px] text-slate-400">{"Placeholders: {ref} {beneficiary} {batch} {seller} — truncated to 30 characters."}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfgOpen(false)} disabled={cfgSaving}>Cancel</Button>
            <Button onClick={saveConfig} disabled={cfgSaving || !((cfgForm.debit_account_number || "").trim())}>
              {cfgSaving ? "Saving…" : "Save settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout batches list */}
      <Dialog open={batchesOpen} onOpenChange={setBatchesOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>COD payout batches</DialogTitle>
          </DialogHeader>
          {batchesLoading ? (
            <p className="text-sm text-slate-500 py-6">Loading…</p>
          ) : batches.length === 0 ? (
            <p className="text-sm text-slate-500 py-6">No payout files generated yet. Use “Generate payout file” on the Pending payouts card.</p>
          ) : (
            <div className="max-h-[55vh] overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                    <th className="py-2 px-2 font-medium">Batch</th>
                    <th className="py-2 px-2 font-medium whitespace-nowrap">Date</th>
                    <th className="py-2 px-2 font-medium">Sellers</th>
                    <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Net</th>
                    <th className="py-2 px-2 font-medium">Status</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 px-2 font-mono text-slate-700">{b.batch_number}</td>
                      <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{formatToIST(b.created_at)}</td>
                      <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{b.paid_seller_count || 0}/{b.seller_count || 0} paid{b.exception_count ? ` · ${b.exception_count} skipped` : ""}</td>
                      <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{inr(b.total_net)}</td>
                      <td className="py-2 px-2"><BatchStatusBadge status={b.status} /></td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => downloadBatchFile(b.id, b.batch_number)} title="Download ICICI file"><Download className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openBatchDetail(b.id)}>Open</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canPayout && (
            <DialogFooter className="sm:justify-start">
              <Button variant="outline" size="sm" onClick={openConfig}><Settings className="w-4 h-4 mr-1.5" /> Payout settings</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Payout batch detail — per-seller UTR entry */}
      <Dialog open={!!batchDetail} onOpenChange={(v) => !v && setBatchDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Payout batch {batchDetail?.batch_number}
              {batchDetail?.status && <BatchStatusBadge status={batchDetail.status} />}
            </DialogTitle>
          </DialogHeader>
          {batchLoading || !batchDetail?.seller_breakdown ? (
            <p className="text-sm text-slate-500 py-6">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="text-sm text-slate-600">
                  {batchDetail.seller_count} seller(s) · {batchDetail.order_count} order(s) · net <span className="font-semibold text-slate-800">{inr(batchDetail.total_net)}</span>
                  {" · "}paid <span className="font-semibold text-green-700">{inr(batchDetail.paid_net)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadBatchFile(batchDetail.id, batchDetail.batch_number)}>
                    <Download className="w-4 h-4 mr-1.5" /> ICICI file
                  </Button>
                  {canPayout && batchDetail.status === "initiated" && (
                    <Button size="sm" variant="outline" onClick={() => doCancelBatch(batchDetail.id)} disabled={cancelling}
                      className="text-red-600 border-red-200 hover:bg-red-50">
                      <Ban className="w-4 h-4 mr-1.5" /> {cancelling ? "Cancelling…" : "Cancel batch"}
                    </Button>
                  )}
                </div>
              </div>

              <div className="max-h-[45vh] overflow-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {batchDetail.seller_breakdown.map((s) => (
                  <div key={s.seller_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{s.brand_name || s.seller_id}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {s.order_count} order(s) · {s.bank?.account_holder_name || "-"} · <span className="font-mono">{maskAcct(s.bank?.account_number)}</span> · {s.bank?.ifsc}
                      </p>
                    </div>
                    <div className="text-sm font-semibold tabular-nums text-slate-800 whitespace-nowrap">{inr(s.net_amount)}</div>
                    {s.paid ? (
                      <div className="flex items-center gap-1.5 text-xs text-green-700 whitespace-nowrap">
                        <CheckCircle2 className="w-4 h-4" /> Paid · <span className="font-mono">{s.utr}</span>
                      </div>
                    ) : canPayout && batchDetail.status !== "cancelled" ? (
                      <div className="flex items-center gap-2">
                        <Input value={utrInputs[s.seller_id] || ""} onChange={(e) => setUtrInputs((p) => ({ ...p, [s.seller_id]: e.target.value }))}
                          placeholder="Bank UTR" className="h-8 w-36 font-mono text-xs" />
                        <Button size="sm" onClick={() => submitSellerUtr(s.seller_id)} disabled={utrBusy === s.seller_id}>
                          {utrBusy === s.seller_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 whitespace-nowrap">{batchDetail.status === "cancelled" ? "Cancelled" : "Unpaid"}</span>
                    )}
                  </div>
                ))}
              </div>

              {batchDetail.exceptions?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <p className="text-xs font-medium text-amber-800 mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Skipped — no bank details ({batchDetail.exceptions.length})
                  </p>
                  <ul className="text-xs text-amber-800 space-y-0.5">
                    {batchDetail.exceptions.map((e) => (
                      <li key={e.seller_id}>{e.brand_name || e.seller_id} — {e.order_count} order(s) · {inr(e.gross_amount)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2CCodReconciliation;
